import React, { useState, useEffect } from "react";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Pagination,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  TextField,
  MenuItem,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { styled, alpha } from "@mui/material/styles";
import InputBase from "@mui/material/InputBase";
import instance from "../../utils/api";
import Topbar from "../../Layouts/Admin_layout/Admin_topbar";
import { toast } from "react-toastify";
import { FiTrash2, FiEye, FiDownload } from "react-icons/fi";
import CustomDatePicker from "../../Components/Admin/CustomDatePicker";
import dayjs from "dayjs";
import { formatDate } from "../../utils/helper";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": { backgroundColor: alpha(theme.palette.common.white, 0.25) },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: { marginLeft: theme.spacing(1), width: "auto" },
  border: "1px solid #79c245",
  marginRight: "10px",
}));
const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#79c245",
}));
const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  fontFamily: "Poppins,sans-serif",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    [theme.breakpoints.up("sm")]: { width: "12ch", "&:focus": { width: "20ch" } },
  },
}));

function FarmMaintenance() {
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1, currentPage: 1, total: 0 });
  const [addOpen, setAddOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    farmer_name: "",
    phone: "",
    village: "",
    mandal_block: "",
    district: "",
    farm_area_acres: "",
    number_of_palms: "",
    date_of_plantation: null,
    creation_date: dayjs(),
    state: "",
    plantation_status: "Planted",
    ffb_price: "18000",
    measurement_unit: "acre",
    measurement_value: "",
  });

  const resetForm = () => {
    setForm({
      farmer_name: "",
      phone: "",
      village: "",
      mandal_block: "",
      district: "",
      farm_area_acres: "",
      number_of_palms: "",
      date_of_plantation: null,
      creation_date: dayjs(),
      state: "",
      plantation_status: "Planted",
      ffb_price: "18000",
      measurement_unit: "acre",
      measurement_value: "",
    });
  };

  const fetchRecords = async () => {
    try {
      const response = await instance.get(`/farmMaintenance/list?page=${page}&limit=10&search=${search}`);
      setRecords(response.data.data);
      setPagination({
        totalPages: response.data.totalPages,
        currentPage: response.data.currentPage,
        total: response.data.total
      });
    } catch (err) {
      console.log(err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await instance.get("/locations");
      setLocations(response.data.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchRecords();
    fetchLocations();
  }, [page]);

  const handleSearch = () => {
    setPage(1);
    fetchRecords();
  };

  const handleFormChange = (field, value) => {
    let updated = { ...form, [field]: value };

    if (field === 'measurement_unit' || field === 'measurement_value') {
      const unit = field === 'measurement_unit' ? value : form.measurement_unit;
      const val = field === 'measurement_value' ? value : form.measurement_value;

      const numVal = parseFloat(val);
      if (!isNaN(numVal) && numVal > 0) {
        let acres = 0;
        if (unit === 'hectar') {
          acres = numVal * 2.45;
        } else if (unit === 'bigha') {
          acres = numVal * 0.63;
        } else {
          acres = numVal;
        }

        updated.farm_area_acres = String(Number(acres.toFixed(2)));
        updated.number_of_palms = String(Math.round(acres * 50));
      } else {
        updated.farm_area_acres = "";
        updated.number_of_palms = "";
      }
    }

    setForm(updated);
  };

  // When mandal/block is selected from locations dropdown, auto-fill district and state
  const handleMandalSelect = (event, selectedLocation) => {
    if (selectedLocation) {
      setForm({
        ...form,
        mandal_block: selectedLocation.name,
        district: selectedLocation.district_name || "",
        state: selectedLocation.state_name || "",
      });
    } else {
      setForm({ ...form, mandal_block: "", district: "", state: "" });
    }
  };

  const handleSubmit = async () => {
    if (!form.farmer_name || !form.phone || !form.village || !form.district || !form.measurement_value || !form.farm_area_acres || !form.number_of_palms || !form.creation_date) {
      toast.error("Please fill all mandatory fields (*)");
      return;
    }
    if (form.plantation_status === "Planted" && !form.date_of_plantation) {
      toast.error("Please select Date of Plantation");
      return;
    }
    try {
      const payload = {
        ...form,
        date_of_plantation: form.date_of_plantation ? dayjs(form.date_of_plantation).format("YYYY-MM-DD") : "",
        creation_date: form.creation_date ? dayjs(form.creation_date).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")
      };
      const response = await instance.post("/farmMaintenance/create", payload);
      if (response.data.success) {
        toast.success("Farm maintenance record created");
        setAddOpen(false);
        resetForm();
        fetchRecords();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error creating record");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      const response = await instance.delete(`/farmMaintenance/delete?id=${id}`);
      if (response.data.success) {
        toast.success("Record deleted");
        fetchRecords();
      }
    } catch (err) {
      toast.error("Error deleting record");
    }
  };

  const handleDownloadPdf = async (record) => {
    const fileName = `OPFM-${record.farmer_name}-${record.phone}.pdf`;
    try {
      toast.info("Generating PDF...", { autoClose: 2000 });
      const response = await instance.get(`/farmMaintenance/downloadPdf?id=${record._id}`);
      if (response.data.success && response.data.download_url) {
        const fileUrl = response.data.download_url;
        try {
          const fetchRes = await fetch(fileUrl);
          const blob = await fetchRes.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error) {
          // Fallback for CORS issues
          const link = document.createElement('a');
          link.href = fileUrl;
          link.target = '_blank';
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        toast.error("Failed to get PDF download link");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error generating PDF");
    }
  };

  return (
    <div style={{ margin: "20px 20px 0px 0px" }}>
      <Topbar />
      <div className="officer_section">
        <section className="main_box">
          <div className="container-fluid">
            <div className="table_box">
              <TableContainer component={Paper}>
                <div className="table_inbox">
                  <div className="add_user_list">
                    <div className="add_user_content">
                      <div style={{ fontFamily: "Poppins,sans-serif", fontSize: "20px", fontWeight: "bold" }}>
                        Farm Maintenance
                      </div>
                    </div>
                    <div className="add_user" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Search>
                        <SearchIconWrapper><SearchIcon /></SearchIconWrapper>
                        <StyledInputBase
                          placeholder="Search…"
                          inputProps={{ "aria-label": "search" }}
                          type="search"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                      </Search>
                      <Button
                        variant="outlined"
                        onClick={() => setAddOpen(true)}
                        sx={{ borderColor: '#78c144', color: '#78c144', textTransform: 'none', fontFamily: 'Poppins,sans-serif', fontWeight: 'bold' }}
                      >
                        + Add Record
                      </Button>
                    </div>
                  </div>
                  <div className="adduser_table">
                    <Table
                      className="user_table product_table"
                      aria-label="farm maintenance table"
                      style={{ borderCollapse: "separate", minWidth: "1400px" }}
                    >
                      <TableHead>
                        <TableRow className="table_head">
                          <TableCell align="center">Sl.No</TableCell>
                          <TableCell align="center">Farmer Name</TableCell>
                          <TableCell align="center">Phone</TableCell>
                          <TableCell align="center">Village</TableCell>
                          <TableCell align="center">Mandal/Block</TableCell>
                          <TableCell align="center">District</TableCell>
                          <TableCell align="center">Farm Area (Acres)</TableCell>
                          <TableCell align="center">No. of Palms</TableCell>
                          <TableCell align="center">Date of Plantation</TableCell>
                          <TableCell align="center">Created Date</TableCell>
                          <TableCell align="center">State</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="center">Added By</TableCell>
                          <TableCell
                            align="center"
                            style={{
                              position: "sticky",
                              right: 0,
                              zIndex: 10,
                              backgroundColor: "#fff",
                              borderLeft: "1px solid #c6c6c6",
                            }}
                          >
                            Download
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody className="table_body">
                        {records.length > 0 ? (
                          records.map((record, index) => (
                            <TableRow key={record._id}>
                              <TableCell align="center">{(page - 1) * 10 + index + 1}</TableCell>
                              <TableCell align="center">{record.farmer_name}</TableCell>
                              <TableCell align="center">{record.phone}</TableCell>
                              <TableCell align="center">{record.village}</TableCell>
                              <TableCell align="center">{record.mandal_block || "-"}</TableCell>
                              <TableCell align="center">{record.district}</TableCell>
                              <TableCell align="center">{record.farm_area_acres}</TableCell>
                              <TableCell align="center">{record.number_of_palms}</TableCell>
                              <TableCell align="center">{formatDate(record.date_of_plantation)}</TableCell>
                              <TableCell align="center">{formatDate(record.creation_date)}</TableCell>
                              <TableCell align="center">{record.state || "-"}</TableCell>
                              <TableCell align="center">
                                <span style={{ color: record.plantation_status === 'Planted' ? '#78c144' : '#f44336', fontWeight: 'bold', fontSize: '12px' }}>
                                  {record.plantation_status || "Planted"}
                                </span>
                              </TableCell>
                              <TableCell align="center">
                                {record.created_by_name || (record.created_by === "Field Officer" ? "Field Officer" : "Vendor")}
                              </TableCell>
                              <TableCell
                                align="center"
                                style={{
                                  position: "sticky",
                                  right: 0,
                                  zIndex: 5,
                                  backgroundColor: "#fff",
                                  borderLeft: "1px solid #c6c6c6",
                                  boxShadow: "-2px 0 5px rgba(0,0,0,0.03)"
                                }}
                              >
                                <Tooltip title="Download PDF">
                                  <IconButton size="small" onClick={() => handleDownloadPdf(record)} sx={{ color: '#78c144' }}>
                                    <FiDownload />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={12} align="center">
                              <Typography>No records found</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="pagination_table">
                    <Pagination
                      count={pagination.totalPages}
                      page={pagination.currentPage}
                      onChange={(e, val) => setPage(val)}
                      shape="rounded"
                      variant="outlined"
                    />
                  </div>
                </div>
              </TableContainer>
            </div>
          </div>
        </section>
      </div>

      {/* Add Farm Maintenance Dialog */}
      <Dialog open={addOpen} onClose={() => { setAddOpen(false); resetForm(); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Poppins,sans-serif', fontWeight: 'bold', background: '#78c144', color: '#fff' }}>
          Enter Your Farm Details
          <IconButton onClick={() => { setAddOpen(false); resetForm(); }} sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Farmer Name *" value={form.farmer_name}
                onChange={(e) => handleFormChange('farmer_name', e.target.value)}
                placeholder="e.g. Ravi Kumar"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Phone / Mobile Number *" value={form.phone}
                onChange={(e) => { if (/^\d*$/.test(e.target.value) && e.target.value.length <= 10) handleFormChange('phone', e.target.value); }}
                placeholder="e.g. 9876543210"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Village *" value={form.village}
                onChange={(e) => handleFormChange('village', e.target.value)}
                placeholder="e.g. Chamiya"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={locations}
                getOptionLabel={(option) => option.name || ''}
                value={locations.find(l => l.name === form.mandal_block) || null}
                onChange={handleMandalSelect}
                renderInput={(params) => <TextField {...params} label="Mandal / Block" placeholder="Select Mandal/Block" />}
                freeSolo
                onInputChange={(e, value) => {
                  if (!locations.find(l => l.name === value)) {
                    setForm(prev => ({ ...prev, mandal_block: value }));
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="District *" value={form.district}
                onChange={(e) => handleFormChange('district', e.target.value)}
                placeholder="e.g. Bastar"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth select label="Measurement Unit *" value={form.measurement_unit}
                onChange={(e) => handleFormChange('measurement_unit', e.target.value)}
              >
                <MenuItem value="acre">Acre</MenuItem>
                <MenuItem value="hectar">Hectar</MenuItem>
                <MenuItem value="bigha">Bigha</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Measurement Value *" value={form.measurement_value}
                onChange={(e) => handleFormChange('measurement_value', e.target.value)}
                placeholder="e.g. 5"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                disabled
                fullWidth label="Farm Area (Acres)" value={form.farm_area_acres}
                InputProps={{ readOnly: true }}
                placeholder="Calculated automatically"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                disabled
                fullWidth label="Number of Palms (50/acre)" value={form.number_of_palms}
                InputProps={{ readOnly: true }}
                placeholder="Calculated automatically"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth select label="Plantation Status *" value={form.plantation_status}
                onChange={(e) => handleFormChange('plantation_status', e.target.value)}
              >
                <MenuItem value="Planted">Planted</MenuItem>
                <MenuItem value="Not Planted">Not Planted</MenuItem>
              </TextField>
            </Grid>
            {form.plantation_status === "Planted" && (
              <Grid item xs={12} md={6}>
                <CustomDatePicker
                  label="Date of Plantation"
                  value={form.date_of_plantation}
                  onChange={(date) => handleFormChange('date_of_plantation', date)}
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <CustomDatePicker
                label="Created Date"
                value={form.creation_date}
                onChange={(date) => handleFormChange('creation_date', date)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="State" value={form.state}
                onChange={(e) => handleFormChange('state', e.target.value)}
                placeholder="e.g. Chhattisgarh"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="FFB Price (₹/MT)" value={form.ffb_price}
                onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleFormChange('ffb_price', e.target.value); }}
                placeholder="e.g. 18000"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setAddOpen(false); resetForm(); }} variant="outlined" sx={{ color: 'red', borderColor: 'red', textTransform: 'none', fontFamily: 'Poppins,sans-serif' }}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ textTransform: 'none', fontFamily: 'Poppins,sans-serif', background: '#78c144', '&:hover': { background: '#5fa030' } }}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default FarmMaintenance;
